import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Ruler, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState({ newPassword: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pwd.newPassword !== pwd.confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd.newPassword });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm rounded-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none";

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#FDFDFD]">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center rounded-sm">
            <Ruler size={16} className="text-white" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">SCOPEWORKS</span>
        </div>

        {done ? (
          <div data-testid="reset-success">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={22} className="text-emerald-600" strokeWidth={1.5} />
              <h1 className="font-heading text-3xl font-medium tracking-tighter">Password updated</h1>
            </div>
            <p className="text-sm text-zinc-500 mb-8">Your password has been reset. You can now sign in with it.</p>
            <Link to="/login" data-testid="go-to-login"
              className="inline-block bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors">
              Go to sign in
            </Link>
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token">
            <h1 className="font-heading text-3xl font-medium tracking-tighter mb-1">Invalid link</h1>
            <p className="text-sm text-zinc-500 mb-8">This password reset link is missing its token. Request a new one from the sign-in page.</p>
            <Link to="/login" className="text-sm text-[#002FA7] hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={22} className="text-[#002FA7]" strokeWidth={1.5} />
              <h1 className="font-heading text-3xl font-medium tracking-tighter">Reset password</h1>
            </div>
            <p className="text-sm text-zinc-500 mb-8">Choose a new password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-zinc-700 mb-1 block">New password</label>
                <input data-testid="reset-new-password-input" type="password" className={inputCls}
                  value={pwd.newPassword} onChange={(e) => setPwd({ ...pwd, newPassword: e.target.value })} required />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-700 mb-1 block">Confirm new password</label>
                <input data-testid="reset-confirm-password-input" type="password" className={inputCls}
                  value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required />
              </div>
              {error && <p data-testid="reset-error" className="text-xs text-red-600">{error}</p>}
              <button data-testid="reset-password-submit" disabled={busy}
                className="w-full bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium py-2.5 rounded-sm transition-colors disabled:opacity-50">
                {busy ? "Please wait…" : "Reset password"}
              </button>
            </form>
            <Link to="/login" className="mt-6 inline-block text-xs text-zinc-500 hover:text-[#002FA7]">Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
